import React from "react";
import { getSliderTrackBackground, randomId, sliderColors, validateClassName } from "@sebgroup/extract";
import { SliderProps } from "../Input/types";
import "./GreenSlider.scss";
import IconButton from "../../IconButton/IconButton";
import InfoCircle from "../../Icons/InfoCircleIcon";
import { parseFormattedNumber } from "../../../utils/formatters";
import classNames from "classnames";
import { InputWrapper } from "../Input/GreenInput";

const minMaxFormatFn = (value: number | undefined, unit?: string) => {
  if (value === undefined) {
    return `- ${unit}`;
  }

  if (unit) {
    return `${value} ${unit}`;
  }

  return value.toString();
};

export function Slider({
  id = randomId(),
  testId,
  name = `${randomId()}-slider`,
  value,
  maxLength,
  min = 0,
  max,
  minMaxFormat = minMaxFormatFn,
  showMinMax = false,
  step = 1,
  label,
  info,
  hasTextbox = false,
  unitLabel,
  disabled = false,
  onChange,
  onSliderChange,
  onSliderClamp,
  onBlur,
  enterKeyHint,
  slideoutButtonLabel,
  onSlideoutButtonClick,
  validator,
}: SliderProps) {
  const [background, setBackground] = React.useState<string>();
  const sliderValue = parseFormattedNumber(value) ?? min;

  React.useLayoutEffect(() => {
    if (disabled) {
      setBackground(sliderColors.disabled);
      return;
    }

    let percent = 0;
    if (sliderValue !== undefined && max !== undefined) {
      percent = ((sliderValue - min) / (max - min)) * 100;
    }

    setBackground(getSliderTrackBackground(percent));
  }, [disabled, sliderValue, min, max]);

  const greenFormItemClassNames = classNames("gds-form-item", validator && validateClassName(validator?.indicator));

  return (
    <>
      <div className={greenFormItemClassNames}>
        <div className="gds-slider-label-container">
          <div className="gds-slider-label-container__header">
            <div>
              {label && (
                <>
                  <label htmlFor={name} id={`${name}-label`}>
                    {label}
                  </label>
                  {info && <p id={`${id}-info`}>{info}</p>}
                </>
              )}
            </div>
            {onSlideoutButtonClick && (
              <IconButton
                data-testid={`${id}-slideout-button__testid`}
                aria-expanded={false}
                aria-controls={`${id}-slider-info`}
                onClick={onSlideoutButtonClick}
              >
                <InfoCircle title={slideoutButtonLabel} />
              </IconButton>
            )}
          </div>

          {hasTextbox && (
            <InputWrapper unitLabel={unitLabel}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={value}
                id={`${id}-textbox`}
                name={name}
                enterKeyHint={enterKeyHint}
                className={validator ? "is-invalid" : ""}
                disabled={disabled}
                onChange={onChange}
                onBlur={onBlur}
                min={min}
                max={max}
                maxLength={maxLength}
                onInvalid={(e) => {
                  e.preventDefault();
                }}
                title=""
                data-testid={testId}
                aria-invalid={!!validator}
                aria-labelledby={`${name}-label`}
                aria-describedby={validator ? `${id}-error` : `${id}-info`}
              />
            </InputWrapper>
          )}
        </div>
        <input
          type="range"
          value={sliderValue}
          id={id}
          name={name}
          min={min}
          max={max ?? 0}
          step={step}
          disabled={disabled}
          onChange={onSliderChange}
          onMouseUp={onSliderClamp}
          onKeyUp={onSliderClamp}
          onTouchEnd={onSliderClamp}
          style={{
            background,
          }}
          data-testid={`slider-${testId}`}
          aria-invalid={!!validator}
          aria-labelledby={`${name}-label`}
          aria-describedby={validator ? `${id}-error` : `${id}-info`}
        />
        {showMinMax && (
          <div className="gds-slider-min-max">
            <div>{minMaxFormat(min, unitLabel)}</div>
            <div>{minMaxFormat(max, unitLabel)}</div>
          </div>
        )}
        {validator && validator.message && (
          <p className="gds-slider-error-info" id={`${id}-error`}>
            {validator.message}
          </p>
        )}
      </div>
    </>
  );
}

export default Slider;
